/**
 * Password Validation Utility
 * Enforces strong password requirements
 */

export const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {object} { isValid: boolean, errors: string[] }
 */
export const validatePassword = (password) => {
  const errors = [];

  if (!password || password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`);
  }

  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (PASSWORD_REQUIREMENTS.requireNumber && !/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (PASSWORD_REQUIREMENTS.requireSpecialChar && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*...)');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get password strength indicator
 * @param {string} password - Password to check
 * @returns {string} 'weak' | 'medium' | 'strong'
 */
export const getPasswordStrength = (password) => {
  if (!password) return 'weak';

  let strength = 0;
  if (password.length >= 12) strength++;
  if (password.length >= 16) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) strength++;
  if (password.length >= 20) strength++;

  if (strength <= 3) return 'weak';
  if (strength <= 5) return 'medium';
  return 'strong';
};

/**
 * Format password requirements for display
 */
export const getPasswordRequirementsText = () => {
  return [
    `At least ${PASSWORD_REQUIREMENTS.minLength} characters`,
    PASSWORD_REQUIREMENTS.requireUppercase && 'One uppercase letter',
    PASSWORD_REQUIREMENTS.requireLowercase && 'One lowercase letter',
    PASSWORD_REQUIREMENTS.requireNumber && 'One number',
    PASSWORD_REQUIREMENTS.requireSpecialChar && 'One special character',
  ].filter(Boolean).join(', ');
};

